/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Filter, X } from 'lucide-react'
import { getCenters, getDocumentStatuses } from '@/api/invoices/invoices.api'
import type { InvoiceFilters as InvoiceFiltersType } from '@/types/invoices.types'

interface InvoiceFiltersProps {
  filters: InvoiceFiltersType
  onFiltersChange: (filters: InvoiceFiltersType) => void
}

export function InvoiceFilters({ filters, onFiltersChange }: InvoiceFiltersProps) {
  const [open, setOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState<InvoiceFiltersType>(filters)

  // Načtení středisek
  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: getCenters,
  })

  // Načtení statusů
  const { data: statuses } = useQuery({
    queryKey: ['documentStatuses'],
    queryFn: getDocumentStatuses,
  })

  // Synchronizace s props
  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleFilterChange = (key: keyof InvoiceFiltersType, value: string | undefined) => {
    // Pokud je hodnota 'all', nastavíme undefined (= žádný filtr)
    const filterValue = value === 'all' ? undefined : value
    setLocalFilters((prev) => ({ ...prev, [key]: filterValue }))
  }

  const handleApplyFilters = () => {
    onFiltersChange(localFilters)
    setOpen(false)
  }

  const handleClearFilters = () => {
    const clearedFilters: InvoiceFiltersType = {
      page: 1,
      perPage: 20,
    }
    setLocalFilters(clearedFilters)
    onFiltersChange(clearedFilters)
    setOpen(false)
  }

  const hasActiveFilters =
    filters.centerId ||
    filters.statusCode ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.dateDueFrom ||
    filters.dateDueTo

  const activeFiltersCount = [
    filters.centerId,
    filters.statusCode,
    filters.dateFrom || filters.dateTo,
    filters.dateDueFrom || filters.dateDueTo,
  ].filter(Boolean).length

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        <DialogTrigger asChild>
          <Button variant="outline" size="default">
            <Filter className="h-4 w-4 mr-2" />
            Filtry
            {activeFiltersCount > 0 && (
              <span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </DialogTrigger>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-1" />
            Vymazat filtry
          </Button>
        )}
      </div>

      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Filtrovat faktury</DialogTitle>
          <DialogDescription>
            Nastavte filtry pro zobrazení požadovaných faktur
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Středisko */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="filter-center" className="text-right">
              Středisko
            </Label>
            <Select
              value={localFilters.centerId || 'all'}
              onValueChange={(value) => handleFilterChange('centerId', value)}
            >
              <SelectTrigger id="filter-center" className="col-span-3">
                <SelectValue placeholder="Všechna střediska" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechna střediska</SelectItem>
                {centers?.map((center) => (
                  <SelectItem key={center.id} value={center.id}>
                    {center.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="filter-status" className="text-right">
              Status
            </Label>
            <Select
              value={localFilters.statusCode || 'all'}
              onValueChange={(value) => handleFilterChange('statusCode', value)}
            >
              <SelectTrigger id="filter-status" className="col-span-3">
                <SelectValue placeholder="Všechny statusy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny statusy</SelectItem>
                {statuses?.map((status) => (
                  <SelectItem key={status.code} value={status.code}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Datum přijetí */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Datum přijetí</Label>
            <div className="col-span-3 grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="filter-date-from" className="text-xs text-muted-foreground">
                  Od
                </Label>
                <Input
                  id="filter-date-from"
                  type="date"
                  value={localFilters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="filter-date-to" className="text-xs text-muted-foreground">
                  Do
                </Label>
                <Input
                  id="filter-date-to"
                  type="date"
                  value={localFilters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Datum splatnosti */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Datum splatnosti</Label>
            <div className="col-span-3 grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="filter-date-due-from" className="text-xs text-muted-foreground">
                  Od
                </Label>
                <Input
                  id="filter-date-due-from"
                  type="date"
                  value={localFilters.dateDueFrom || ''}
                  onChange={(e) => handleFilterChange('dateDueFrom', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="filter-date-due-to" className="text-xs text-muted-foreground">
                  Do
                </Label>
                <Input
                  id="filter-date-due-to"
                  type="date"
                  value={localFilters.dateDueTo || ''}
                  onChange={(e) => handleFilterChange('dateDueTo', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Zrušit
          </Button>
          <Button type="button" onClick={handleApplyFilters}>
            Použít filtry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

